import { fg } from '@atlaskit/platform-feature-flags/fg';

/** Entry points that start a Create with Rovo session, mirroring `isCreateWithRovoSend`. */
const CREATE_WITH_ROVO_SOURCES = ['ai-first-creation', 'chat-only-creation-modal'];

/**
 * `cwr` means a Confluence Create-with-Rovo session and the backend routes agent selection on it,
 * so a host that declares a creation source of its own is running a different experience and must
 * not be relabelled — the staging area is shared UI, not a Confluence signal. Gated because
 * AdminHub is the only host declaring another source today, so the labelling is unchanged for every
 * existing surface while the gate is off.
 */
const hasOwnCreationSource = (creationContextSource: string | undefined): boolean =>
	creationContextSource !== undefined &&
	!CREATE_WITH_ROVO_SOURCES.includes(creationContextSource) &&
	fg('adminhub-create-policy-with-rovo');

export function getCreationContextExperience({
	contentContextValue,
	isFirstPrompt,
	source,
	creationContextSource,
	existingExperience,
	existingContentTypes,
	chatContextContentType,
	isRemixAction,
	isViewMode,
	contentMauiId,
	mediaFileId,
	isRemixExperienceContextEnabled = true,
	isCreationContextContractEnabled = false,
}: {
	contentContextValue: string;
	isFirstPrompt: boolean;
	source: string | undefined;
	creationContextSource: string | undefined;
	existingExperience: string | undefined;
	existingContentTypes: string[] | undefined;
	chatContextContentType: string | undefined;
	isRemixAction: boolean | undefined;
	isViewMode: boolean;
	contentMauiId?: string;
	mediaFileId?: string;
	isRemixExperienceContextEnabled?: boolean;
	isCreationContextContractEnabled?: boolean;
}):
	| { experience: 'chat_edit' | 'chat_view' | 'cwr_edit' | 'remix_edit'; contentTypes: string[] }
	| { experience: 'cwr' | 'remix_edit' }
	| undefined {
	const resolvedContentTypes =
		existingContentTypes && existingContentTypes.length > 0
			? existingContentTypes
			: chatContextContentType
				? [chatContextContentType]
				: undefined;

	if (
		isRemixExperienceContextEnabled &&
		existingExperience === 'inline_edit' &&
		(contentMauiId || mediaFileId)
	) {
		// A remix edit is identified by the block being edited, not by the content hosting it, so
		// it must still be reported when no content type can be resolved.
		if (isCreationContextContractEnabled) {
			return { experience: 'remix_edit' };
		}

		return resolvedContentTypes
			? {
					experience: 'remix_edit',
					contentTypes: resolvedContentTypes,
				}
			: undefined;
	}

	if (existingExperience === 'cwr_edit') {
		if (resolvedContentTypes) {
			return {
				experience: 'cwr_edit',
				contentTypes: resolvedContentTypes,
			};
		}

		return { experience: 'cwr' };
	}

	if (existingExperience) {
		return undefined;
	}

	// The host-source check comes last so the gate is only read for a send that would otherwise be
	// labelled, rather than on every Confluence send.
	const isCwrStagingAreaFollowUp =
		contentContextValue === 'staging-area' &&
		!isFirstPrompt &&
		source !== 'remix' &&
		creationContextSource !== 'remix' &&
		!isRemixAction &&
		!hasOwnCreationSource(creationContextSource);

	if (isCwrStagingAreaFollowUp) {
		if (chatContextContentType) {
			return {
				experience: 'cwr_edit',
				contentTypes: [chatContextContentType],
			};
		}

		return { experience: 'cwr' };
	}

	// When both the contract gate and the new live page gate are on: a first-prompt in the
	// staging-area with the draft editor open is a chat_edit — the user is asking Rovo to
	// modify the current draft.
	const isStagingAreaChatEdit =
		fg('confluence_aifc_new_live_page_chat_context') &&
		contentContextValue === 'staging-area' &&
		isFirstPrompt &&
		source !== 'remix' &&
		creationContextSource !== 'remix' &&
		!isRemixAction;

	if (isStagingAreaChatEdit && chatContextContentType) {
		return {
			experience: 'chat_edit',
			contentTypes: [chatContextContentType],
		};
	}

	if (contentContextValue === 'global' && chatContextContentType) {
		return {
			experience: isViewMode ? 'chat_view' : 'chat_edit',
			contentTypes: [chatContextContentType],
		};
	}

	return undefined;
}
